import { usePage } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormError } from "@/components/FormError"

interface PageProps {
  fieldErrors?: Record<string, string>
  [key: string]: unknown
}

export default function ResetPassword() {
  const { t } = useTranslation()
  const { url, props } = usePage<PageProps>()
  const { fieldErrors } = props
  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [confirmError, setConfirmError] = useState<string | null>(null)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    if (password !== confirmPassword) {
      e.preventDefault()
      setConfirmError(t("resetPassword.passwordsMustMatch"))
      return
    }
    setConfirmError(null)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t("resetPassword.title")}</CardTitle>
          <CardDescription>{t("resetPassword.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form method="post" action={url} onSubmit={handleSubmit} className="space-y-4">
            <input type="hidden" name="authenticity_token" value={csrfToken} />
            <div className="space-y-2">
              <Label htmlFor="password">{t("resetPassword.passwordLabel")}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <FormError error={fieldErrors?.password} />
              <p className="text-xs text-muted-foreground">{t("resetPassword.passwordHint")}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password-confirm">{t("resetPassword.confirmPasswordLabel")}</Label>
              <Input
                id="password-confirm"
                name="password-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value)
                  setConfirmError(null)
                }}
                required
                minLength={8}
              />
              <FormError error={confirmError || fieldErrors?.["password-confirm"]} />
            </div>

            <Button type="submit" className="w-full">
              {t("resetPassword.submit")}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <a href="/login" className="text-foreground hover:underline">
                {t("resetPassword.backToLogin")}
              </a>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
